import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom, forkJoin, of, Observable } from 'rxjs';
import { first, map, catchError } from 'rxjs/operators';

import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { TimesheetsService, AssignedSubmissionRow } from '../../service/entity-services/timesheets.service';
import { UsersService } from '../../service/entity-services/users.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';
import { normalizeEmail } from '../../shared/utils/normalize-email';
import { environment } from '../../../environments/environment';

/** Which work-list tab a row is rendered under. Drives the available actions. */
export type QueueTab = 'review' | 'approval' | 'payment';

/** One aggregated activity line shown on a queue card (hours + dollar amount). */
export interface ActivityLine {
  name: string;
  hours: number;
  amount: number;
  tier: 'primary' | 'supplemental' | 'premium';
}

/**
 * My Work → Provider Timesheets reviewer/approver/payment work-list.
 *
 * Calls `GET /submissions/assigned/{userEmail}`, which returns the caller's
 * submissions already bucketed by the backend (status + the caller's role on
 * the spec) into three tabs:
 *   For My Review        — status 'submitted', caller is the spec reviewer
 *   For My Approval      — status 'under_review', caller is an approver
 *   Approved For Payment — status 'approved'/'pending_payment', caller is accounting
 *
 * Actions POST to the discrete cbhi back-half routes `/approvals/{review,approve,
 * reject,bounce,pending-payment,paid}/{submissionId}` (§7). The actor is derived
 * from the ID token (NO changedBy); reject/bounce carry a required note. The
 * backend guards every transition (403 wrong actor, 409 status race, 400
 * NOTE_REQUIRED). Actions wired here (canonical §3 vocab):
 *   review:   Approve → /approvals/review (→ under_review) | Return to owner → /approvals/reject (→ rejected, note)
 *   approval: Approve → /approvals/approve (→ approved, minApprovals-gated) | Reject → /approvals/reject (note)
 *   payment:  Process Payment → /approvals/pending-payment | Mark Paid → /approvals/paid | Bounce → /approvals/bounce (note)
 *
 * The queue actor is always a reviewer/approver/accountant — never the owner — so
 * the negative action is reject (review/approval) or accountant bounce (payment),
 * both note-required. Owner recall/reopen live elsewhere (the spec/submission view),
 * not in this queue.
 */
@Component({
  selector: 'app-timesheet-approval-queue',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatTabsModule, MatCardModule, MatButtonModule, MatIconModule, MatTooltipModule,
    MatFormFieldModule, MatInputModule, MatProgressSpinnerModule, MatSnackBarModule
  ],
  templateUrl: './timesheet-approval-queue.component.html',
  styleUrls: ['./timesheet-approval-queue.component.css']
})
export class TimesheetApprovalQueueComponent implements OnInit {
  private timesheetsService = inject(TimesheetsService);
  private usersService = inject(UsersService);
  private permissionsStore = inject(PermissionsStoreService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);

  loading = signal(true);
  acting = signal(false);
  reviewQueue = signal<AssignedSubmissionRow[]>([]);
  approvalQueue = signal<AssignedSubmissionRow[]>([]);
  paymentQueue = signal<AssignedSubmissionRow[]>([]);

  /** Payment stage toggle — off for orgs paying via NetSuite (e.g. cbhi). When
      false the "Approved For Payment" tab is hidden; `approved` is terminal. */
  readonly showPaymentStage = (environment as any).timesheetPaymentStage !== false;

  /** submissionId of the row whose note box is open (for reject/bounce). */
  activeKey = signal<string | null>(null);
  noteText = '';

  /** Per-row check number entered on the payment tab, keyed by submissionId. */
  checkNumbers = signal<Record<string, string>>({});

  /** Hours-by-duty breakdown per submissionId, filled in after the queue loads. */
  breakdowns = signal<Record<string, ActivityLine[]>>({});

  /**
   * Whether per-activity dollar amounts are trustworthy for a submission —
   * true only when a real rate was resolved for every contributing tier. When
   * false we show hours only and never fabricate amounts (payment-safety).
   */
  amountsReliable = signal<Record<string, boolean>>({});

  /** True when the queue is showing the org-wide System-Administrator view. */
  adminView = signal(false);
  /** Backend hit its row cap — the list on screen is NOT the whole queue. */
  truncated = signal(false);

  private myEmail = '';

  ngOnInit(): void {
    this.load();
  }

  private isEmptyResult(r: { forReview: any[]; forApproval: any[]; forPayment: any[] }): boolean {
    return !r.forReview.length && !r.forApproval.length && !r.forPayment.length;
  }

  /** Only a System Administrator (NOT a plain Administrator) sees every submission. */
  private isSystemAdmin(): boolean {
    const role = (this.permissionsStore.permissions()?.role ?? '').toLowerCase().trim();
    return role === 'system administrator';
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.activeKey.set(null);
    try {
      this.myEmail = normalizeEmail(
        await firstValueFrom(this.usersService.currentUserEmail$.pipe(first()))
      );

      // System Administrator ONLY: see EVERY submission still awaiting action
      // (all HCPs, all periods), not just their own assignments. The backend
      // endpoint gates the role and returns the same bucketed shape, so the tabs
      // and cards below render unchanged. Same pattern as the timecard queue.
      let result;
      if (this.isSystemAdmin()) {
        this.adminView.set(true);
        result = await firstValueFrom(this.timesheetsService.getActiveAdminSubmissions(this.myEmail));

        // Until the admin endpoint is deployed it fails soft to empty buckets, which
        // would BLANK the queue for a System Administrator who is also a real
        // reviewer/approver. So an empty admin result falls back to their personal
        // assignments — an admin never sees less than they did before. Once the
        // endpoint is live this fallback only fires when the org genuinely has no
        // submissions awaiting action, where both calls return empty anyway.
        if (this.isEmptyResult(result) && this.myEmail) {
          const mine = await firstValueFrom(this.timesheetsService.getAssignedSubmissions(this.myEmail));
          if (!this.isEmptyResult(mine)) {
            this.adminView.set(false);
            result = mine;
          }
        }
      } else {
        this.adminView.set(false);
        if (!this.myEmail) {
          this.reviewQueue.set([]); this.approvalQueue.set([]); this.paymentQueue.set([]);
          return;
        }
        result = await firstValueFrom(this.timesheetsService.getAssignedSubmissions(this.myEmail));
      }
      this.truncated.set(result.truncated === true);
      this.reviewQueue.set(result.forReview);
      this.approvalQueue.set(result.forApproval);
      this.paymentQueue.set(result.forPayment);
      // Fire-and-forget: cards render immediately, the hours-by-duty breakdown
      // fills in as each submission's days are fetched.
      this.breakdowns.set({});
      this.loadBreakdowns([...result.forReview, ...result.forApproval, ...result.forPayment]);
      // …and, for an org that pays outside the app, replace the Approved tab's
      // contents with the status-driven list (see loadApprovedPipeline).
      this.loadApprovedPipeline();
    } catch (err) {
      console.error('[TimesheetApprovalQueue] load failed', err);
      this.snackBar.open('Could not load your timesheet queue.', 'Dismiss', { duration: 4000 });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * The "Approved" tab for an org that pays outside the app (cbhi → NetSuite).
   *
   * There, `approved` is terminal: nobody is assigned, so these rows are absent
   * from the assigned/admin work list by design — that is the whole point of the
   * fix (an approved timesheet used to sit on the accountant's My Work forever
   * with no action able to clear it). But "approved, waiting on NetSuite" is
   * still something an admin needs to see, so the tab is fed by STATUS instead
   * of by assignment.
   *
   * System Administrators only — it's an org-wide payment-state view, and an
   * individual reviewer has no reason to see every other contract's totals.
   * Everyone else simply doesn't get the tab.
   *
   * Fire-and-forget and fails soft: the three work tabs are already on screen.
   *
   * Note these rows are deliberately NOT passed to loadBreakdowns — that costs
   * two requests per row, and unlike the work queues this list only grows (it
   * clears when payments are recorded). The card still shows period, hours and
   * the period total.
   */
  private async loadApprovedPipeline(): Promise<void> {
    if (this.showPaymentStage || !this.isSystemAdmin()) return;
    try {
      const approved = await firstValueFrom(this.timesheetsService.getApprovedAwaitingPayment());
      this.paymentQueue.set(approved);
    } catch (err) {
      console.error('[TimesheetApprovalQueue] approved pipeline load failed', err);
    }
  }

  /** Open the existing timesheet detail for a submission's spec. */
  openDetail(row: AssignedSubmissionRow): void {
    if (!row.specId) {
      this.snackBar.open('This submission has no linked spec to open.', 'Dismiss', { duration: 3000 });
      return;
    }
    this.router.navigate(['/securehome/timesheet-spec', row.specId]);
  }

  // ── Actions ──────────────────────────────────────────────

  /** Positive action for the review/approval tabs → the matching /approvals/* route. */
  approve(row: AssignedSubmissionRow, tab: QueueTab): void {
    const id = row.submissionId;
    if (tab === 'review') {
      this.runAction(this.timesheetsService.reviewApproveSubmission(id), 'Approved — sent to the approver.');
    } else if (tab === 'approval') {
      this.runAction(this.timesheetsService.approveSubmission(id), 'Approved.');
    }
  }

  // ── Payment: single combined "approve for payment & mark paid" ───────────

  /** Current check-number text for a payment row. */
  checkNumberFor(row: AssignedSubmissionRow): string {
    return this.checkNumbers()[row.submissionId] ?? '';
  }

  setCheckNumber(row: AssignedSubmissionRow, value: string): void {
    this.checkNumbers.update(m => ({ ...m, [row.submissionId]: value }));
  }

  /** Enables the combined payment button — any non-empty (trimmed) value. */
  hasCheckNumber(row: AssignedSubmissionRow): boolean {
    return this.checkNumberFor(row).trim().length > 0;
  }

  private clearCheckNumber(submissionId: string): void {
    this.checkNumbers.update(m => {
      const next = { ...m };
      delete next[submissionId];
      return next;
    });
  }

  /**
   * One-click payment finalize: drives the row all the way to `paid` and
   * persists the check number. From `approved` it does both backend steps
   * (pending_payment → paid); from `pending_payment` it just does paid. The
   * check number rides on the paid call so it's stored with the submission.
   * Disabled in the template until a check number is present.
   */
  async payAndMarkPaid(row: AssignedSubmissionRow): Promise<void> {
    if (this.acting()) return;
    const checkNumber = this.checkNumberFor(row).trim();
    if (!checkNumber) {
      this.snackBar.open('Enter a check number before approving payment.', 'Dismiss', { duration: 4000 });
      return;
    }
    this.acting.set(true);
    try {
      const id = row.submissionId;
      if (row.status === 'approved') {
        // approved → pending_payment, then → paid. If the second step fails the
        // row stays in pending_payment and the button re-offers just the paid step.
        await firstValueFrom(this.timesheetsService.markPendingPayment(id));
      }
      await firstValueFrom(this.timesheetsService.markPaid(id, checkNumber));
      this.snackBar.open('Approved for payment and marked paid.', 'Dismiss', { duration: 3000 });
      this.clearCheckNumber(id);
      await this.load();
    } catch (err: any) {
      console.error('[TimesheetApprovalQueue] payAndMarkPaid failed', err);
      await this.handleActionError(err);
    } finally {
      this.acting.set(false);
    }
  }

  /** Negative action — opens the required-note box (reject for review/approval, bounce for payment). */
  startNegative(row: AssignedSubmissionRow): void {
    this.noteText = '';
    this.activeKey.set(row.submissionId);
  }

  cancelNegative(): void {
    this.activeKey.set(null);
    this.noteText = '';
  }

  confirmNegative(row: AssignedSubmissionRow, tab: QueueTab): void {
    // Reject (review/approval) and accountant bounce (payment) both require a note (§2).
    if (!this.noteText.trim()) {
      this.snackBar.open('A note is required for this action.', 'Dismiss', { duration: 4000 });
      return;
    }
    const id = row.submissionId;
    if (tab === 'payment') {
      this.runAction(this.timesheetsService.bounceSubmission(id, this.noteText), 'Bounced — restarting approval.');
    } else {
      this.runAction(this.timesheetsService.rejectSubmission(id, this.noteText), 'Rejected — returned to the owner.');
    }
  }

  negativeLabel(tab: QueueTab): string {
    if (tab === 'review') return 'Return to owner';
    if (tab === 'payment') return 'Bounce (start over)';
    return 'Reject';
  }

  /** True when this row can offer a negative action. Payment can bounce from approved OR pending_payment (§2). */
  canReject(row: AssignedSubmissionRow, tab: QueueTab): boolean {
    // Bounce is an accountant action. Orgs with no in-app payment stage (cbhi →
    // NetSuite) see these rows read-only, so no negative action either.
    if (tab === 'payment' && !this.showPaymentStage) return false;
    if (tab === 'payment') return row.status === 'approved' || row.status === 'pending_payment';
    return true;
  }

  isOpen(row: AssignedSubmissionRow): boolean {
    return this.activeKey() === row.submissionId;
  }

  private async runAction(action: Observable<any>, successMsg: string): Promise<void> {
    if (this.acting()) return;
    this.acting.set(true);
    try {
      await firstValueFrom(action);
      this.snackBar.open(successMsg, 'Dismiss', { duration: 3000 });
      this.activeKey.set(null);
      this.noteText = '';
      await this.load();
    } catch (err: any) {
      console.error('[TimesheetApprovalQueue] action failed', err);
      await this.handleActionError(err);
    } finally {
      this.acting.set(false);
    }
  }

  /**
   * Map the canonical approval-action errors to admin-friendly feedback (§6/§7):
   *   403 — caller isn't in this stage's actor list
   *   409 — STATUS_CONFLICT: someone else already acted (optimistic-write race) → reload
   *   400 NOTE_REQUIRED — reject/bounce without a note
   */
  private async handleActionError(err: any): Promise<void> {
    const status = err?.status;
    const code = err?.error?.code || err?.error?.errorType;
    const detail = err?.error?.message || err?.message || 'Please try again.';
    if (status === 403) {
      this.snackBar.open("You're not assigned to act on this item.", 'Dismiss', { duration: 5000 });
    } else if (status === 409) {
      this.snackBar.open('Someone already acted on this — refreshing the queue.', 'Dismiss', { duration: 5000 });
      this.activeKey.set(null);
      this.noteText = '';
      await this.load();
    } else if (status === 400 && /note/i.test(`${code} ${detail}`)) {
      this.snackBar.open('A note is required for this action.', 'Dismiss', { duration: 5000 });
    } else {
      this.snackBar.open(`Action failed. ${detail}`, 'Dismiss', { duration: 6000 });
    }
  }

  // ── Hours-by-duty breakdown ──────────────────────────────

  /**
   * Fetch each row's full submission and aggregate hours per activity. Best
   * effort and non-blocking — a failed fetch just leaves that card without a
   * breakdown. The work list is the caller's own queue, so this is a small,
   * bounded fan-out.
   */
  private async loadBreakdowns(rows: AssignedSubmissionRow[]): Promise<void> {
    const toFetch = rows.filter(r => r.submissionId && r.contractId && r.dateOfRecord);
    if (!toFetch.length) return;
    try {
      const results = await firstValueFrom(forkJoin(
        toFetch.map(r =>
          // Fetch the submission (hours per activity) AND the version-correct
          // rates (financials, spec-id keyed) so we can show each activity's
          // dollar contribution to the period total.
          forkJoin({
            sub: this.timesheetsService.getTimesheetSubmission(r.contractId, r.dateOfRecord!)
              .pipe(catchError(() => of(null))),
            fin: r.specId
              ? this.timesheetsService.getTimesheetSpecFinancials(r.specId, r.dateOfRecord!)
                  .pipe(catchError(() => of(null)))
              : of(null),
            spec: r.specId
              ? this.timesheetsService.getTimesheetSpec(r.specId).pipe(catchError(() => of(null)))
              : of(null)
          }).pipe(
            map(({ sub, fin, spec }) => {
              const { lines, reliable } = this.aggregateActivities(sub, fin, spec);
              return { id: r.submissionId, lines, reliable };
            }),
            catchError(() => of({ id: r.submissionId, lines: [] as ActivityLine[], reliable: false }))
          )
        )
      ));
      const nextLines: Record<string, ActivityLine[]> = {};
      const nextReliable: Record<string, boolean> = {};
      for (const { id, lines, reliable } of results) {
        nextLines[id] = lines;
        nextReliable[id] = reliable;
      }
      this.breakdowns.set(nextLines);
      this.amountsReliable.set(nextReliable);
    } catch {
      // Leave whatever we have; the cards still render with totals.
    }
  }

  /**
   * Sum hours per activity across all days, and compute each activity's dollar
   * contribution = hours × the rate for its tier. If rates are unavailable,
   * fall back to splitting the authoritative period total proportionally by
   * hours so the lines always reconcile to the amount being approved.
   */
  private aggregateActivities(sub: any, fin: any, spec: any): { lines: ActivityLine[]; reliable: boolean } {
    // Rate per tier. Best source is the backend's own `totals` — within a tier
    // every activity shares one rate, so tierAmount / tierHours is the EXACT
    // rate the backend used, and per-activity amounts reconcile to it perfectly.
    // Fall back to the spec snapshot, then financials, then the live spec.
    const totals = sub?.totals;
    const snap = sub?.specSnapshot;
    const rateFor = (hoursKey: string, amountKey: string, ...fallbacks: any[]): number => {
      const h = parseFloat(totals?.[hoursKey]);
      const a = parseFloat(totals?.[amountKey]);
      if (!isNaN(h) && h > 0 && !isNaN(a)) return a / h;
      return this.pickRate(...fallbacks);
    };
    const rates = {
      primary: rateFor('primaryHours', 'primaryAmount',
        snap?.primaryHourlyRate, fin?.primaryHourlyRate, spec?.primaryHourlyRate),
      supplemental: rateFor('supplementalHours', 'supplementalAmount',
        snap?.supplementalHourlyRate, fin?.supplementalHourlyRate, spec?.supplementalHourlyRate),
      premium: rateFor('premiumHours', 'premiumAmount',
        snap?.premiumHourlyRate, fin?.premiumHourlyRate, spec?.premiumHourlyRate)
    };

    const acc = new Map<string, { hours: number; tier: 'primary' | 'supplemental' | 'premium' }>();
    const cats: [string, 'primary' | 'supplemental' | 'premium'][] = [
      ['primaryActivities', 'primary'],
      ['supplementalActivities', 'supplemental'],
      ['premiumActivities', 'premium']
    ];
    const days = sub?.days;
    if (days && typeof days === 'object') {
      for (const day of Object.values<any>(days)) {
        for (const [cat, tier] of cats) {
          const acts = day?.[cat];
          if (acts && typeof acts === 'object') {
            for (const [name, val] of Object.entries(acts)) {
              const hrs = parseFloat(val as any) || 0;
              if (!hrs) continue;
              const existing = acc.get(name);
              if (existing) existing.hours += hrs;
              else acc.set(name, { hours: hrs, tier });
            }
          }
        }
      }
    }

    const lines: ActivityLine[] = [...acc.entries()].map(([name, { hours, tier }]) => ({
      name,
      hours: Math.round(hours * 100) / 100,
      tier,
      amount: Math.round(hours * rates[tier] * 100) / 100
    })).sort((a, b) => b.hours - a.hours);

    // Amounts are trustworthy ONLY when a real rate was resolved for every tier
    // that actually has hours. If any contributing tier's rate is missing, we
    // do NOT show amounts — we never fabricate a dollar figure for payment.
    const reliable = lines.length > 0 && lines.every(l => rates[l.tier] > 0);

    return { lines, reliable };
  }

  /** First positive rate among the candidates (financials, then spec). */
  private pickRate(...candidates: any[]): number {
    for (const c of candidates) {
      const n = parseFloat(c);
      if (!isNaN(n) && n > 0) return n;
    }
    return 0;
  }

  /** Whether per-activity dollar amounts can be trusted for this row's submission. */
  amountsReliableFor(row: AssignedSubmissionRow): boolean {
    return this.amountsReliable()[row.submissionId] === true;
  }

  breakdownFor(row: AssignedSubmissionRow): ActivityLine[] {
    return this.breakdowns()[row.submissionId] || [];
  }

  // ── Display helpers ──────────────────────────────────────

  partyName(row: AssignedSubmissionRow): string {
    return row.reportingPartyName?.trim() || row.reportingParty?.trim() || '(unknown party)';
  }

  period(row: AssignedSubmissionRow): string {
    if (row.periodStart && row.periodEnd) return `${row.periodStart} – ${row.periodEnd}`;
    return row.periodStart || row.periodEnd || row.dateOfRecord || '';
  }

  statusLabel(status: string | undefined): string {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatHours(n: number | undefined | null): string {
    return n == null ? '0' : String(n);
  }

  formatAmount(n: number | undefined | null): string {
    return n == null ? '' : `$${Number(n).toFixed(2)}`;
  }
}
